// src/stores/auth.js
import { defineStore } from "pinia";

// Use the same env var you already use elsewhere (Vue CLI)
const API_BASE = process.env.VUE_APP_API_BASE_URL || "";

if (!API_BASE) {
  // Optional: one-time warning so you catch misconfig in prod
  // eslint-disable-next-line no-console
  console.warn(
    "[auth store] VUE_APP_API_BASE_URL is empty; calls will go to relative /api/*"
  );
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    user: null,
    loading: false,
    error: "",
  }),
  getters: {
    isAuthed: (s) => {
      const u = s.user || {};
      return !!(u.id || u._id || u.userId || u.email);
    },
    displayName: (s) =>
      s.user?.name || s.user?.contactName || s.user?.email || "Account",
    initials: (s) => {
      const n =
        s.user?.name?.trim() ||
        s.user?.contactName?.trim() ||
        s.user?.email ||
        "";
      const parts = n.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return "U";
    },
  },
  actions: {
    async login(email, password) {
      this.loading = true;
      this.error = "";

      try {
        const res = await fetch(`${API_BASE}/api/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email, password }),
        });

        const data = await res.json();

        if (res.ok) {
          // ✅ CRITICAL FIX: Populate the user object here!
          // Assuming your API response ('data') contains user details
          this.user = {
            id: data.id || data._id, // Use whatever ID your backend provides
            name: data.name || data.email.split("@")[0], // Use full name if available
            // 👇 This is the property for the avatar/initial logic in the Navbar
            // Set to null/undefined or check for an empty string if your API does that
            avatar: data.avatarUrl || data.profilePic || null,
          };
          return true; // Login successful
        } else {
          this.error = data.message || `Login failed: ${res.status}`;
          this.user = null;
          return false; // Login failed
        }
      } catch (e) {
        this.error = e?.message || "Failed to connect to API";
        this.user = null;
        return false; // Login failed
      } finally {
        this.loading = false;
      }
    },
    async init() {
      this.loading = true;
      this.error = "";
      try {
        const res = await fetch(`${API_BASE}/api/users/me`, {
          credentials: "include",
        });
        if (res.ok) {
          this.user = await res.json();
        } else if (res.status === 401) {
          this.user = null;
        } else {
          this.error = `users/me failed: ${res.status}`;
          // keep whatever user we already had (e.g., from login response)
        }
      } catch (e) {
        this.error = e?.message || "Failed to load session";
        // keep existing user on network errors
      } finally {
        this.loading = false;
      }
    },
    async logout() {
      this.error = "";
      try {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: "POST",
          credentials: "include",
        });
        // Optional: if you want to assert success, check the response here.
      } catch {
        // ignore network errors on logout
      } finally {
        this.user = null;
      }
    },
  },
});
